<script setup>
import { Link } from '@inertiajs/vue3'
import PersonCard from '@/Shared/PersonCard.vue'
import Icon from '@/Shared/Icon.vue'

const props = defineProps({
  items: {
    type: Array,
    required: true
  },
  nameField: {
    type: String,
    default: 'name'
  },
  lastnameField: {
    type: String,
    default: 'lastname'
  },
  phoneField: {
    type: String,
    default: 'phone'
  },
  nestedFields: {
    type: Boolean,
    default: false
  },
  nestedKey: {
    type: String,
    default: 'player'
  },
  badgeConfig: {
    type: Object,
    default: () => ({
      field: null,
      condition: null,
      trueText: '',
      falseText: '',
      trueIcon: null,
      falseIcon: null,
      trueClass: 'bg-indigo-100 text-indigo-800',
      falseClass: 'bg-gray-100 text-gray-800'
    })
  },
  roleConfig: {
    type: Object,
    default: () => ({
      field: null,
      condition: null,
      trueText: '',
      falseText: ''
    })
  },
  basePath: {
    type: String,
    required: true
  },
  emptyState: {
    type: Object,
    default: () => ({
      title: 'No hay elementos',
      description: 'Crea un nuevo elemento para comenzar',
      buttonText: 'Nuevo elemento',
      buttonUrl: '',
      icon: 'Warning'
    })
  }
});

function getPersonData(item) {
  return props.nestedFields ? item[props.nestedKey] : item;
}

function getBadge(item) {
  if (!props.badgeConfig.field) return null;
  
  const value = item[props.badgeConfig.field];
  const condition = props.badgeConfig.condition !== null ? value === props.badgeConfig.condition : !!value;
  
  return {
    show: true,
    text: condition ? props.badgeConfig.trueText : props.badgeConfig.falseText,
    icon: condition ? props.badgeConfig.trueIcon : props.badgeConfig.falseIcon,
    class: condition ? props.badgeConfig.trueClass : props.badgeConfig.falseClass
  };
}

function getRole(item) {
  if (!props.roleConfig.field) return '';
  
  const value = item[props.roleConfig.field];
  const condition = props.roleConfig.condition !== null ? value === props.roleConfig.condition : !!value;
  
  return condition ? props.roleConfig.trueText : props.roleConfig.falseText;
}

function getEditUrl(item) {
  return `${props.basePath}/${item.id}/edit`;
}
</script>

<template>
  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    <!-- Tarjetas para cada elemento -->
    <PersonCard
      v-for="item in items"
      :key="item.id"
      :person="getPersonData(item)"
      :name-field="nameField"
      :lastname-field="lastnameField"
      :phone-field="phoneField"
      :badge="getBadge(item)"
      :role="getRole(item)"
      :edit-url="getEditUrl(item)"
    />

    <!-- Estado vacío -->
    <div 
      v-if="items.length === 0"
      class="col-span-full bg-white rounded-xl shadow-sm border border-gray-200 p-10 text-center"
    >
      <div class="flex flex-col items-center justify-center max-w-md mx-auto">
        <div class="bg-indigo-50 p-4 rounded-full mb-4">
          <Icon :name="emptyState.icon" class="h-10 w-10 text-indigo-400" />
        </div>
        <h3 class="text-xl font-semibold text-gray-900 mb-2">{{ emptyState.title }}</h3>
        <p class="text-gray-500 mb-6">{{ emptyState.description }}</p>
        <Link class="btn-indigo flex items-center justify-center" :href="emptyState.buttonUrl">
          <Icon name="Plus" class="h-5 w-5 mr-2" />
          <span>{{ emptyState.buttonText }}</span>
        </Link>
      </div>
    </div>
  </div>
</template>
